import * as Yup from 'yup';
import { ChoicesOrderUnitMeasure } from '@/sections/administrative/accounts/orders/context';
import { IBillingOrderCurrentItem } from '@/types';

interface CustomValidateOptions {
  index?: number;
  context?: {
    unitMeasure?: string[];
    itemsCurrentOrder?: IBillingOrderCurrentItem[];
    getQuantityToUse?: (item: IBillingOrderCurrentItem) => number;
  };
}

export const createBillingOrderSecondStepSchema = (
  t: (key: string) => string,
  watchAllFields?: any,
  itemsCurrentOrder?: IBillingOrderCurrentItem[],
  getQuantityToUse?: (item: IBillingOrderCurrentItem) => number,
  orderSubtotal?: number
) => {
  return Yup.object().shape({
    legalEntity: Yup.array().min(1, t('We need at least one legal entity to proceed')),
    items: Yup.array().of(
      Yup.object().shape({
        value: Yup.array().of(
          Yup.number()
            .required(t('This item needs a value'))
            .test(
              'validate-value',
              t("Hmm, this value doesn't look right"),
              function Validator(value) {
                const { path, parent, options } = this as {
                  path: string;
                  parent: any;
                  options: CustomValidateOptions;
                };
                const index = parent && options.index;
                const correspondingUnit = watchAllFields?.unitMeasure?.[index];
                let messageError = null;
                const currentItem = itemsCurrentOrder?.[index];

                switch (correspondingUnit) {
                  case ChoicesOrderUnitMeasure.percentageAmount:
                    if (value < 0 || value > 100) {
                      messageError = t(`Percentages should be between 0% and 100%`);
                    }
                    break;
                  case ChoicesOrderUnitMeasure.quantity:
                    if (value < 0) {
                      messageError = t(
                        `Quantities can't be negative - let's use a positive number`
                      );
                    } else if (value % 1 !== 0) {
                      messageError = t(`Let's use whole numbers for quantities (no decimals)`);
                    } else if (currentItem && getQuantityToUse) {
                      const maxQuantity = getQuantityToUse(currentItem);

                      if (maxQuantity && value > maxQuantity) {
                        messageError = t(
                          `That's more than what's available. The maximum is ${maxQuantity}`
                        );
                      }
                    }
                    break;

                  case ChoicesOrderUnitMeasure.amount:
                    if (currentItem?.milestone?.total && value > currentItem.milestone.total) {
                      messageError = t(`The amount can't exceed the resource total`);
                    }
                    break;

                  default:
                    break;
                }

                if (messageError) {
                  return this.createError({
                    path,
                    message: messageError,
                  });
                }
                return true;
              }
            )
        ),
      })
    ),

    unitMeasure: Yup.array()
      .of(Yup.string().required(t('Please select how you want to distribute this')))
      .min(1, t('We need to know the distribution unit')),

    enableDiscounts: Yup.boolean(),

    discounts: Yup.array()
      .of(
        Yup.object().shape({
          amount: Yup.number()
            .required(t('Discount amount is required'))
            .min(0.01, t('Discount amount must be greater than 0'))
            .test('max-discount', t('Discount cannot exceed subtotal'), function (value) {
              if (!orderSubtotal || !value) return true;
              return value <= orderSubtotal;
            }),
          description: Yup.string()
            .required(t('Discount description is required'))
            .min(5, t('Description must be at least 5 characters'))
            .trim(),
        })
      )
      .when('enableDiscounts', {
        is: true,
        then: (schema) => schema.min(1, t('Please add at least one discount or disable discounts')),
        otherwise: (schema) => schema,
      })
      .test(
        'total-discount-validation',
        t('Total discounts cannot exceed the subtotal'),
        function (discounts) {
          if (!discounts || !orderSubtotal) return true;

          const totalDiscounts = discounts.reduce(
            (sum, discount) => sum + (discount?.amount || 0),
            0
          );

          return totalDiscounts <= orderSubtotal;
        }
      ),

    currentDiscount: Yup.object().shape({
      amount: Yup.number().min(0, t('Amount must be positive')),
      description: Yup.string(),
    }),
  });
};
